import { useLang } from "@/contexts/LangContext";
import SectionReveal from "@/components/SectionReveal";
import {
  Stamp,
  PlaneLanding,
  Palmtree,
  Briefcase,
  GraduationCap,
  Users,
  ExternalLink,
  Sparkles,
  CheckCircle2,
  ArrowUpRight,
} from "lucide-react";

const VISA_CENTER_URL =
  "https://www.visaforchina.cn/DXB3_EN/qianzhengyewu";

const VISA_TYPES = [
  {
    icon: Stamp,
    code: "VISA-FREE",
    name: "UAE Passport",
    duration: "Up to 30 days",
    headline: "No visa required",
    body: "Under the China–UAE mutual exemption agreement, UAE ordinary-passport holders enter visa-free for tourism, business or family visits.",
    badge: "Most popular",
    badgeColor: "bg-[#C62828]",
  },
  {
    icon: PlaneLanding,
    code: "240H TRANSIT",
    name: "10-Day Transit",
    duration: "240 hours",
    headline: "Transit without visa",
    body: "Passport holders from 55 countries (incl. UK, US, EU, GCC, Japan, Korea, Australia) can transit visa-free for up to 10 days via 65 ports. Onward third-country flight required.",
    badge: "New 2025",
    badgeColor: "bg-[#2E5D4B]",
  },
  {
    icon: Palmtree,
    code: "VISA L",
    name: "Tourist Visa",
    duration: "30–90 days",
    headline: "Leisure & sightseeing",
    body: "For travelers from countries not on the visa-free list. Single, double or multiple entries available. Apply via the Chinese Visa Application Service Center (Dubai).",
  },
  {
    icon: Briefcase,
    code: "VISA M",
    name: "Business Visa",
    duration: "30–180 days",
    headline: "Trade & meetings",
    body: "For commercial activities including the Canton Fair, supplier visits and B2B negotiations. We provide invitation-letter support for our Business Meeting & Canton Fair clients.",
  },
  {
    icon: GraduationCap,
    code: "VISA X1 / X2",
    name: "Student Visa",
    duration: "Long / short stay",
    headline: "For Mandarin programs",
    body: "X1 for stays >180 days (e.g. HSK degree tracks); X2 for short courses. We assist our Mandarin Language Trip clients with school admission letters and JW202 forms.",
  },
  {
    icon: Users,
    code: "VISA Q / S",
    name: "Family Visit",
    duration: "30–180 days",
    headline: "Visit relatives in China",
    body: "Q1/Q2 for Chinese citizens' or PR holders' relatives. S1/S2 for foreign workers' families. We help compile invitation and relationship documents.",
  },
];

const LATEST_UPDATES = [
  {
    when: "Nov 5, 2025",
    title: "240-hour transit ports expanded",
    body: "China expanded the visa-free transit network from 60 to 65 entry/exit ports — Beijing, Shanghai, Guangzhou, Chengdu, Xi'an, Hangzhou and many more.",
  },
  {
    when: "Jun 12, 2025",
    title: "Bahrain, Kuwait, Oman & Saudi Arabia added",
    body: "Four additional GCC countries received 30-day visa-free access to China (UAE was already exempt since 2018 under the mutual agreement).",
  },
  {
    when: "2024–2025",
    title: "Apple Pay, Visa & Mastercard on Alipay / WeChat",
    body: "Foreign credit cards are now widely accepted in China's super-apps — far easier for travelers than in previous years.",
  },
];

const Visa = () => {
  const { t } = useLang();

  const openVisaCenter = () => {
    window.open(VISA_CENTER_URL, "_blank", "noopener");
  };

  return (
    <section id="visa" data-testid="visa-section" className="bg-[#F7F5F0] py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <SectionReveal className="max-w-3xl">
          <p className="text-xs md:text-sm uppercase tracking-[0.3em] text-[#C62828] font-semibold mb-4">
            ✦ Visa Information · 2025/2026 Update
          </p>
          <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl tracking-tight text-[#121212] leading-[1.05]">
            Your visa, made simple
          </h2>
          <p className="mt-6 text-lg text-[#4A4A4A] leading-relaxed">
            China has dramatically opened its borders in 2024–2025. For most of our clients
            from the UAE and GCC, entering China is now <strong>visa-free</strong>. Below is
            everything you need to know — with a direct link to the official Chinese Visa
            Application Service Center in Dubai.
          </p>
        </SectionReveal>

        {/* Hero highlight — UAE visa-free */}
        <SectionReveal delay={0.1} className="mt-12">
          <div className="relative overflow-hidden bg-gradient-to-br from-[#0E2A5C] via-[#0E2A5C] to-[#1B3D7A] text-white p-8 md:p-14 border border-[#D4AF37]/30">
            <div className="absolute -top-20 -right-20 w-72 h-72 bg-[#D4AF37]/15 rounded-full blur-3xl" />
            <div className="absolute -bottom-20 -left-20 w-72 h-72 bg-[#C62828]/20 rounded-full blur-3xl" />
            <div className="relative grid lg:grid-cols-12 gap-8 items-center">
              <div className="lg:col-span-7">
                <p className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] font-semibold mb-3 inline-flex items-center gap-2">
                  <Sparkles size={14} /> For UAE residents
                </p>
                <h3 className="font-heading text-3xl md:text-5xl leading-tight">
                  No visa required for trips up to{" "}
                  <span className="text-[#D4AF37]">30 days.</span>
                </h3>
                <p className="mt-5 text-white/80 leading-relaxed max-w-2xl">
                  Under the China–UAE mutual exemption agreement (in force since 2018,
                  reaffirmed in 2025), UAE ordinary-passport holders simply book the flight
                  and go. No application, no embassy visit, no fees.
                </p>
                <ul className="mt-6 space-y-2.5 text-sm text-white/85">
                  <li className="flex items-start gap-2.5">
                    <CheckCircle2 size={17} className="text-[#3BFFC2] mt-0.5 flex-shrink-0" />
                    Tourism, business meetings, family visits all covered
                  </li>
                  <li className="flex items-start gap-2.5">
                    <CheckCircle2 size={17} className="text-[#3BFFC2] mt-0.5 flex-shrink-0" />
                    Bring passport (6+ months validity) and return/onward ticket
                  </li>
                  <li className="flex items-start gap-2.5">
                    <CheckCircle2 size={17} className="text-[#3BFFC2] mt-0.5 flex-shrink-0" />
                    For stays over 30 days, apply for the relevant visa category below
                  </li>
                </ul>
              </div>
              <div className="lg:col-span-5">
                <div className="bg-white/[0.06] backdrop-blur-sm border border-white/15 p-7">
                  <p className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] font-semibold mb-4">
                    Quick checklist
                  </p>
                  <ul className="space-y-3 text-sm">
                    {[
                      "Passport valid 6+ months",
                      "Confirmed return/onward flight",
                      "Hotel booking or invitation letter",
                      "Travel insurance recommended",
                      "Health declaration on arrival (Customs)",
                    ].map((c) => (
                      <li
                        key={c}
                        className="flex items-start gap-2.5 text-white/85"
                      >
                        <span className="w-1.5 h-1.5 bg-[#D4AF37] rounded-full mt-2 flex-shrink-0" />
                        {c}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </SectionReveal>

        {/* Visa types grid */}
        <SectionReveal delay={0.2} className="mt-16">
          <p className="text-xs uppercase tracking-[0.3em] text-[#0E2A5C] font-semibold mb-3">
            All visa categories
          </p>
          <h3 className="font-heading text-3xl md:text-4xl text-[#121212]">
            Which one applies to you?
          </h3>
        </SectionReveal>

        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {VISA_TYPES.map((v, i) => {
            const Icon = v.icon;
            return (
              <SectionReveal
                key={v.code}
                delay={i * 0.06}
                data-testid={`visa-card-${i}`}
                className="relative bg-white border border-[#EAE6DF] p-7 md:p-8 hover:border-[#C62828]/30 hover:-translate-y-1 transition-all duration-500 group"
              >
                {v.badge && (
                  <span
                    className={`absolute -top-2.5 right-5 ${v.badgeColor} text-white text-[10px] font-bold uppercase tracking-wider px-2.5 py-1`}
                  >
                    {v.badge}
                  </span>
                )}
                <div className="w-12 h-12 flex items-center justify-center bg-[#C62828]/10 text-[#C62828] group-hover:bg-[#C62828] group-hover:text-white transition-colors duration-500">
                  <Icon size={22} strokeWidth={1.5} />
                </div>
                <p className="mt-5 text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-bold">
                  {v.code}
                </p>
                <h4 className="mt-1 font-heading text-2xl text-[#121212]">{v.name}</h4>
                <p className="text-xs uppercase tracking-wider text-[#4A4A4A] mt-1">
                  {v.duration}
                </p>
                <p className="mt-4 font-heading text-base text-[#0E2A5C]">{v.headline}</p>
                <p className="mt-2 text-sm text-[#4A4A4A] leading-relaxed">{v.body}</p>
              </SectionReveal>
            );
          })}
        </div>

        {/* Latest updates timeline */}
        <SectionReveal delay={0.15} className="mt-20">
          <p className="text-xs uppercase tracking-[0.3em] text-[#0E2A5C] font-semibold mb-3">
            What changed recently
          </p>
          <h3 className="font-heading text-3xl md:text-4xl text-[#121212]">
            Latest 2025 policy updates
          </h3>
          <div className="mt-10 grid md:grid-cols-3 gap-6">
            {LATEST_UPDATES.map((u, i) => (
              <div
                key={i}
                data-testid={`visa-update-${i}`}
                className="bg-[#EAE6DF] border-l-4 border-[#C62828] p-6 md:p-7"
              >
                <p className="text-[10px] uppercase tracking-[0.25em] text-[#C62828] font-bold">
                  {u.when}
                </p>
                <h4 className="mt-2 font-heading text-xl text-[#121212]">{u.title}</h4>
                <p className="mt-2 text-sm text-[#4A4A4A] leading-relaxed">{u.body}</p>
              </div>
            ))}
          </div>
        </SectionReveal>

        {/* CTA — Official Visa Center */}
        <SectionReveal delay={0.2} className="mt-16">
          <div className="bg-[#0A0A0A] text-white p-8 md:p-12 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div className="max-w-2xl">
              <p className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] font-semibold mb-3">
                Official partner
              </p>
              <h3 className="font-heading text-2xl md:text-3xl">
                Apply at the Chinese Visa Application Service Center · Dubai
              </h3>
              <p className="mt-3 text-white/70 leading-relaxed">
                Need a Tourist, Business, Student or Family visa? Apply directly through the
                official application center in Dubai. Our team can also handle the paperwork
                on your behalf — just message us on WhatsApp.
              </p>
            </div>
            <button
              data-testid="visa-center-cta"
              onClick={openVisaCenter}
              className="group bg-[#D4AF37] hover:bg-[#bf9a2b] text-[#0A0A0A] px-7 py-4 uppercase tracking-wider text-sm font-bold inline-flex items-center gap-3 transition-colors whitespace-nowrap"
            >
              Open Visa Center
              <ArrowUpRight
                size={16}
                className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform"
              />
            </button>
          </div>
        </SectionReveal>
      </div>
    </section>
  );
};

export default Visa;
